'use client';

import { useState, useTransition } from 'react';
import Link from 'next/link';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { updateDancerProfileAction } from '@kit/artists/server';
import { Button } from '@kit/ui/button';
import { toast } from '@kit/ui/sonner';

/**
 * Owner-only nudge shown at the very top of the dancer's own profile while
 * `is_profile_public` is false: a private profile is invisible to directors
 * and organizers, so the banner pushes the dancer to flip it public in one
 * click. The full visibility matrix (per-section toggles) lives in the
 * Privacy & Visibility settings page, linked alongside.
 *
 * Replaces the in-rail DancerPrivacyToggle (removed 2026-07-04): the
 * always-visible switch was admin-flavoured chrome on an editorial page;
 * the banner only appears when there is something to act on.
 */
export function DancerPrivateBanner() {
  const { t } = useTranslation();
  const [madePublic, setMadePublic] = useState(false);
  const [isPending, startTransition] = useTransition();

  if (madePublic) {
    return null;
  }

  function onMakePublic() {
    startTransition(async () => {
      try {
        const result = await updateDancerProfileAction({
          professional: { isProfilePublic: true },
        });
        if (result && 'success' in result && result.success === false) {
          throw new Error('update failed');
        }
        setMadePublic(true);
        toast.success(
          t('dancer:privacyToggle.nowPublic', {
            defaultValue: 'Your profile is now public.',
          }),
        );
      } catch {
        toast.error(
          t('dancer:privacyToggle.error', {
            defaultValue: 'Could not update visibility. Please try again.',
          }),
        );
      }
    });
  }

  return (
    <div className="border-primary/30 bg-primary/10 border-b px-6 py-3 sm:px-10 lg:px-16">
      <div className="mx-auto flex max-w-[1320px] flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-foreground inline-flex items-center gap-2.5 text-sm leading-snug">
          <Lock className="size-4 shrink-0" aria-hidden="true" />
          {t('dancer:privateBanner.message', {
            defaultValue:
              'Your profile is private — recruiters and organizers cannot find you.',
          })}
        </p>
        <div className="flex shrink-0 items-center gap-4">
          <Button size="sm" disabled={isPending} onClick={onMakePublic}>
            {t('dancer:privateBanner.cta', {
              defaultValue: 'Make profile public',
            })}
          </Button>
          <Link
            href="/home/artist/profile/visibility"
            className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-4 transition-colors"
          >
            {t('dancer:privateBanner.settingsLink', {
              defaultValue: 'Visibility settings',
            })}
          </Link>
        </div>
      </div>
    </div>
  );
}
